import React, { useMemo, useState } from 'react';
import {
  Building2,
  Globe,
  Palette,
  Languages,
  Bell,
  Heart,
  Info,
  Mail,
  Link2,
  Lock,
  Monitor,
  Sun,
  Moon,
  Laptop,
  CheckCircle2,
  Server,
} from 'lucide-react';
import {
  PageHeader,
  StatusBadge,
  DonutChart,
  HorizontalBars,
  EmptyState,
  DemoDataBanner,
} from '../components/ui/UIComponents';
import { usePreferences, PREFS_KEY } from '../context/PreferencesContext';
import { useI18n } from '../i18n/useI18n';

// Variables plateforme — lecture seule (pas de modification domaine libre)
const PLATFORM_VARS = [
  { key: 'platform_name', label: 'Nom de la plateforme', value: 'DSI Academy', type: 'Texte', access: 'readonly' },
  { key: 'platform_short_name', label: 'Nom court', value: 'DSI', type: 'Texte', access: 'readonly' },
  { key: 'platform_url', label: 'URL plateforme', value: 'https://dsiacademy.com', type: 'URL', access: 'readonly' },
  { key: 'admin_portal_url', label: 'URL portail Admin', value: '—', type: 'URL', access: 'protected' },
  { key: 'contact_email', label: 'Email de contact', value: 'contact@dsiacademy.com', type: 'Email', access: 'readonly' },
  { key: 'primary_domain', label: 'Domaine principal', value: 'dsiacademy.com', type: 'Domaine', access: 'infrastructure' },
];

const HEALTH_WORKERS = [
  { name: 'Worker Admin', status: 'up', note: 'Observé (démo locale)' },
  { name: 'Worker Paiement', status: 'prep', note: 'Préparation' },
  { name: 'Worker Contenu', status: 'prep', note: 'Préparation' },
  { name: 'Worker Boutique', status: 'prep', note: 'Préparation' },
  { name: 'Worker Marketplace', status: 'prep', note: 'Préparation' },
  { name: 'Worker Communauté', status: 'prep', note: 'Préparation' },
  { name: 'Worker Notification', status: 'future', note: 'Futur' },
];

const CHART_COLORS = ['#10b981', '#f59e0b', '#f43f5e', '#94a3b8'];

export default function Settings() {
  const [tab, setTab] = useState('general');
  const { prefs, updatePref: setPref } = usePreferences();
  const { t } = useI18n();
  const [savedMsg, setSavedMsg] = useState(null);

  const updatePref = (key, value) => {
    setPref(key, value);
    setSavedMsg(t('settings.savedLocal'));
    setTimeout(() => setSavedMsg(null), 2200);
  };

  const TABS = [
    { id: 'general', label: t('settings.general'), icon: Building2 },
    { id: 'platform', label: t('settings.platform'), icon: Globe },
    { id: 'appearance', label: t('settings.appearance'), icon: Palette },
    { id: 'locale', label: t('settings.locale'), icon: Languages },
    { id: 'prefs', label: t('settings.prefs'), icon: Bell },
    { id: 'health', label: t('settings.health'), icon: Heart },
    { id: 'tech', label: t('settings.tech'), icon: Info },
  ];

  const healthSegments = useMemo(() => {
    const counts = { up: 0, prep: 0, down: 0, future: 0 };
    HEALTH_WORKERS.forEach((w) => {
      if (counts[w.status] !== undefined) counts[w.status] += 1;
      else counts.future += 1;
    });
    return [
      { label: 'Opérationnel', value: counts.up, color: CHART_COLORS[0] },
      { label: 'Préparation', value: counts.prep, color: CHART_COLORS[1] },
      { label: 'Indisponible', value: counts.down, color: CHART_COLORS[2] },
      { label: 'Futur / non configuré', value: counts.future, color: CHART_COLORS[3] },
    ].filter((s) => s.value > 0);
  }, []);

  const healthBars = useMemo(
    () =>
      HEALTH_WORKERS.map((w) => ({
        label: w.name.replace('Worker ', ''),
        value: w.status === 'up' ? 100 : w.status === 'prep' ? 40 : w.status === 'future' ? 10 : 0,
        color: w.status === 'up' ? 'bg-emerald-500' : w.status === 'prep' ? 'bg-amber-500' : 'bg-slate-300',
      })),
    []
  );

  const accessBadge = (access) => {
    const map = {
      readonly: { text: 'Lecture seule', cls: 'bg-slate-100 text-slate-600 border-slate-200' },
      protected: { text: 'Protégée', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
      infrastructure: { text: 'Infrastructure', cls: 'bg-rose-50 text-rose-700 border-rose-200' },
      editable: { text: 'Modifiable', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    };
    const cfg = map[access] || map.readonly;
    return (
      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${cfg.cls}`}>{cfg.text}</span>
    );
  };

  return (
    <div className="space-y-6 fade-up">
      <PageHeader
        title={t('pages.settings.title')}
        subtitle={t('pages.settings.subtitle')}
        workerName="Portail Admin"
      />

      {savedMsg && (
        <div className="p-3 rounded-2xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-bold flex items-center gap-2">
          <CheckCircle2 size={14} /> {savedMsg}
        </div>
      )}

      <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-600">
        La <strong>maintenance globale</strong> et le contrôle sensible des services se gèrent dans{' '}
        <strong>Configuration → Centre de contrôle</strong>. Cette page ne duplique pas ces actions.
      </div>

      <div className="flex flex-wrap gap-1 bg-white border border-slate-200 p-1.5 rounded-2xl">
        {TABS.map((item) => {
          const Icon = item.icon;
          return (
            <button
              key={item.id}
              onClick={() => setTab(item.id)}
              className={`px-3 py-2 rounded-xl text-xs font-semibold flex items-center gap-1.5 transition-all ${
                tab === item.id ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-50'
              }`}
            >
              <Icon size={14} /> {item.labelKey ? t(item.labelKey) : item.label}
            </button>
          );
        })}
      </div>

      {/* GÉNÉRAL */}
      {tab === 'general' && (
        <div className="space-y-4">
          <div className="dsi-card p-6 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-50 via-white to-slate-50 pointer-events-none" />
            <div className="relative flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-indigo-600 text-white flex items-center justify-center font-black text-lg shadow-lg shadow-indigo-600/20">
                DSI
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-black text-slate-900 tracking-tight">DSI Academy</h3>
                <p className="text-sm text-slate-500">Digital Service International</p>
                <div className="mt-3 flex flex-wrap gap-3 text-xs text-slate-600">
                  <span className="inline-flex items-center gap-1"><Globe size={12} /> dsiacademy.com</span>
                  <span className="inline-flex items-center gap-1"><Mail size={12} /> contact@dsiacademy.com</span>
                </div>
              </div>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                <span className="w-2 h-2 rounded-full bg-emerald-500" /> Plateforme opérationnelle
              </span>
            </div>
            <p className="relative text-[10px] text-slate-400 mt-4">
              Identité institutionnelle — valeurs affichées en lecture seule (pas de modification domaine libre).
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[
              ['Nom court', 'DSI'],
              ['Statut observé', 'Opérationnel'],
              ['Portail', 'Admin Control Center'],
            ].map(([k, v]) => (
              <div key={k} className="dsi-card p-4">
                <p className="text-[10px] uppercase text-slate-500 font-semibold">{k}</p>
                <p className="text-sm font-bold text-slate-900 mt-1">{v}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* PLATEFORME */}
      {tab === 'platform' && (
        <div className="space-y-4">
          <p className="text-xs text-slate-500">
            Variables affichables. Le domaine principal est géré par l’infrastructure DNS — pas de bouton « modifier »
            libre. Les variables modifiables le seront uniquement via un contrat backend sécurisé.
          </p>
          <div className="dsi-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 text-[11px] uppercase text-slate-500 font-semibold border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3">Variable</th>
                    <th className="px-4 py-3">Valeur</th>
                    <th className="px-4 py-3">Type</th>
                    <th className="px-4 py-3">État</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {PLATFORM_VARS.map((v) => (
                    <tr key={v.key} className="hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <p className="font-semibold text-slate-900">{v.label}</p>
                        <p className="text-[11px] font-mono text-slate-400">{v.key}</p>
                      </td>
                      <td className="px-4 py-3 font-medium text-slate-800">{v.value}</td>
                      <td className="px-4 py-3 text-slate-500">{v.type}</td>
                      <td className="px-4 py-3">{accessBadge(v.access)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="dsi-card p-4 flex items-start gap-3">
            <Lock size={16} className="text-slate-400 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-semibold text-slate-900">Domaine principal</p>
              <p className="text-xs text-slate-500 mt-0.5">
                <strong>dsiacademy.com</strong> — géré par l’infrastructure. Aucune modification libre côté formulaire
                Admin.
              </p>
            </div>
          </div>

          <div className="dsi-card p-4">
            <div className="flex items-center gap-2 mb-2">
              <Link2 size={14} className="text-indigo-600" />
              <p className="text-sm font-semibold text-slate-900">Liens institutionnels</p>
            </div>
            <p className="text-xs text-slate-500">
              Support, WhatsApp officiel, URLs portail : modifiables uniquement lorsqu’un contrat backend sécurisé le
              permettra. Affichage lecture seule pour l’instant.
            </p>
          </div>
        </div>
      )}

      {/* APPARANCE */}
      {tab === 'appearance' && (
        <div className="space-y-4">
          <p className="text-xs text-slate-500">
            Préférence enregistrée localement. La propagation globale du thème sur tout le portail sera faite après
            validation de cette page.
          </p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { id: 'normal', label: t('settings.themeNormal'), desc: 'Validated current look', icon: Monitor },
              { id: 'light', label: t('settings.themeLight'), desc: 'Bright interface', icon: Sun },
              { id: 'dark', label: t('settings.themeDark'), desc: 'Premium token system', icon: Moon },
              { id: 'system', label: t('settings.themeSystem'), desc: 'Follow device', icon: Laptop },
            ].map((themeOpt) => {
              const Icon = themeOpt.icon;
              const active = prefs.theme === themeOpt.id;
              return (
                <button
                  key={themeOpt.id}
                  onClick={() => updatePref('theme', themeOpt.id)}
                  className={`dsi-card p-4 text-left transition-all ${
                    active ? 'ring-2 ring-indigo-500 border-indigo-300' : 'hover:border-slate-300'
                  }`}
                >
                  <Icon size={18} className={active ? 'text-indigo-600' : 'text-slate-400'} />
                  <p className="text-sm font-bold text-slate-900 mt-2">{themeOpt.label}</p>
                  <p className="text-[11px] text-slate-500 mt-0.5">{themeOpt.desc}</p>
                  {active && <p className="text-[10px] font-bold text-indigo-600 mt-2">Sélectionné</p>}
                </button>
              );
            })}
          </div>
          <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-4 text-xs text-slate-500">
            Le thème sombre doit utiliser des variables de design (<code>--background</code>, <code>--surface</code>,
            etc.) — pas un simple inversion noir/blanc. Application globale après validation.
          </div>
        </div>
      )}

      {/* LANGUE & FORMATS */}
      {tab === 'locale' && (
        <div className="space-y-4">
          <div className="dsi-card p-5 space-y-4">
            <h3 className="text-sm font-semibold text-slate-900">Langue du portail</h3>
            <div className="grid grid-cols-2 gap-3">
              {[
                { id: 'fr', label: 'Français', flag: '🇫🇷' },
                { id: 'en', label: 'English', flag: '🇬🇧' },
              ].map((l) => (
                <button
                  key={l.id}
                  onClick={() => updatePref('language', l.id)}
                  className={`rounded-2xl border p-4 text-left ${
                    prefs.language === l.id ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 bg-white'
                  }`}
                >
                  <span className="text-xl">{l.flag}</span>
                  <p className="text-sm font-bold text-slate-900 mt-1">{l.label}</p>
                  {prefs.language === l.id && (
                    <p className="text-[10px] font-bold text-indigo-600 mt-1">Préférence locale</p>
                  )}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-amber-700 font-medium">
              Préparation uniquement — pas de traduction partielle du projet. Généralisation FR/EN après validation.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="dsi-card p-4">
              <p className="text-[10px] uppercase text-slate-500 font-semibold">Fuseau horaire</p>
              <select
                className="dsi-input mt-2"
                value={prefs.timezone}
                onChange={(e) => updatePref('timezone', e.target.value)}
              >
                <option value="UTC">GMT / UTC+0</option>
                <option value="Africa/Abidjan">Africa/Abidjan</option>
                <option value="Africa/Lagos">Africa/Lagos</option>
              </select>
              <p className="text-[10px] text-slate-400 mt-1">Préférence d’affichage</p>
            </div>
            <div className="dsi-card p-4">
              <p className="text-[10px] uppercase text-slate-500 font-semibold">Format de date</p>
              <select
                className="dsi-input mt-2"
                value={prefs.dateFormat}
                onChange={(e) => updatePref('dateFormat', e.target.value)}
              >
                <option value="DD/MM/YYYY">JJ/MM/AAAA</option>
                <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                <option value="YYYY-MM-DD">AAAA-MM-JJ</option>
              </select>
            </div>
            <div className="dsi-card p-4">
              <p className="text-[10px] uppercase text-slate-500 font-semibold">Devise</p>
              <p className="text-lg font-bold text-slate-900 mt-2">FCFA</p>
              <p className="text-[10px] text-slate-400 mt-1">Valeur plateforme (fixe pour l’instant)</p>
            </div>
          </div>
        </div>
      )}

      {/* PRÉFÉRENCES */}
      {tab === 'prefs' && (
        <div className="space-y-4">
          <div className="dsi-card p-5 space-y-4">
            <h3 className="text-sm font-semibold text-slate-900">Densité d’affichage</h3>
            <div className="grid grid-cols-3 gap-2">
              {[
                { id: 'comfortable', label: t('settings.comfortable') },
                { id: 'standard', label: t('settings.standard') },
                { id: 'compact', label: t('settings.compact') },
              ].map((d) => (
                <button
                  key={d.id}
                  onClick={() => updatePref('density', d.id)}
                  className={`rounded-xl border px-3 py-3 text-xs font-semibold ${
                    prefs.density === d.id ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-slate-200 text-slate-600'
                  }`}
                >
                  {d.label}
                </button>
              ))}
            </div>
          </div>

          <div className="dsi-card p-5 space-y-3">
            {[
              {
                key: 'compactTables',
                label: 'Affichage compact des tableaux',
                description: 'Réduit l’espacement des lignes',
                value: prefs.compactTables,
              },
              {
                key: 'adminAlerts',
                label: 'Alertes administratives',
                description: 'Préférence locale de notification',
                value: prefs.adminAlerts,
              },
            ].map((item) => (
              <div key={item.key} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                <div>
                  <p className="text-sm font-bold text-slate-900">{item.label}</p>
                  <p className="text-[11px] text-slate-500">{item.description}</p>
                </div>
                <button
                  type="button"
                  className={`${item.value ? 'bg-indigo-600' : 'bg-slate-200'} relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 border-transparent transition-colors`}
                  onClick={() => updatePref(item.key, !item.value)}
                >
                  <span
                    className={`${item.value ? 'translate-x-4' : 'translate-x-0'} inline-block h-4 w-4 transform rounded-full bg-white shadow transition`}
                  />
                </button>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-slate-400">Préférences personnelles — stockées localement, distinctes des paramètres métier globaux.</p>
        </div>
      )}

      {/* SANTÉ */}
      {tab === 'health' && (
        <div className="space-y-4">
          <DemoDataBanner workerName="Routes /health Workers" />
          <p className="text-xs text-slate-500">
            Observation préparatoire. Le frontend ne contacte pas librement tous les Workers : le Worker Admin reste
            l’orchestrateur. Aucun historique de disponibilité inventé.
          </p>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <div className="dsi-card p-5">
              <h3 className="text-sm font-semibold text-slate-900 mb-3">Répartition des états</h3>
              <DonutChart
                segments={healthSegments}
                centerValue={HEALTH_WORKERS.length}
                centerLabel="Workers"
              />
            </div>
            <div className="dsi-card p-5">
              <h3 className="text-sm font-semibold text-slate-900 mb-3">Disponibilité indicative</h3>
              <HorizontalBars rows={healthBars} />
              <p className="text-[10px] text-amber-700 mt-2 font-medium">Démo visuelle — pas de sondes live inventées</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {HEALTH_WORKERS.map((w) => (
              <div key={w.name} className="dsi-card p-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-slate-900">{w.name}</p>
                  <span
                    className={`w-2.5 h-2.5 rounded-full ${
                      w.status === 'up' ? 'bg-emerald-500' : w.status === 'prep' ? 'bg-amber-400' : 'bg-slate-300'
                    }`}
                  />
                </div>
                <p className="text-[11px] text-slate-500 mt-1">{w.note}</p>
                <StatusBadge
                  statusKey={w.status === 'up' ? 'ACTIF' : w.status === 'prep' ? 'EN_ATTENTE' : 'INACTIF'}
                  text={w.status === 'up' ? 'UP' : w.status === 'prep' ? 'Préparation' : 'Futur'}
                />
              </div>
            ))}
          </div>

          <div className="dsi-card p-4">
            <EmptyState
              title="Historique de disponibilité non disponible"
              message="Les données 24h apparaîtront lorsqu’un mécanisme réel de suivi sera connecté via Worker Admin."
            />
          </div>
        </div>
      )}

      {/* TECH */}
      {tab === 'tech' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              ['Portail', 'Admin Control Center'],
              ['Environnement', 'Production / Admin'],
              ['Préférences locales', PREFS_KEY],
              ['Thème sélectionné', prefs.theme],
              ['Langue sélectionnée', prefs.language === 'fr' ? 'Français' : 'English'],
              ['Dernière actualisation page', new Date().toLocaleString('fr-FR')],
            ].map(([k, v]) => (
              <div key={k} className="dsi-card p-4">
                <p className="text-[10px] uppercase text-slate-500 font-semibold">{k}</p>
                <p className="text-sm font-bold text-slate-900 mt-1 break-all">{v}</p>
              </div>
            ))}
          </div>
          <div className="dsi-card p-4 flex items-start gap-3">
            <Server size={16} className="text-slate-400 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-slate-900">Version API Worker Admin</p>
              <p className="text-xs text-slate-500 mt-0.5">
                Consulter le <strong>Centre de contrôle</strong> (Configuration) pour la version réellement retournée
                par <code>getParametres</code>.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
